import { Box, Button, TextField, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { Business } from "lib/features/business/types";
import {
  useCreateListMutation,
  useUpdateListMutation,
} from "lib/features/businessList/businessListService";
import { BusinessList, BusinessListDTO } from "lib/features/businessList/types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createBusinessListStyles } from "styles/BusinessListDirectory/createBusinessList.styles";
import * as Yup from "yup";
import Label from "../NewItemForm/components/Label";
import PostScript from "../NewItemForm/components/PostScript";
import BusinessActionCard from "./BusinessActionCard";

interface CreateListFormProps {
  userId: number;
  setShowBusinesses: React.Dispatch<React.SetStateAction<boolean>>;
  selectedBusinesses: Business[];
  handleBusinessRecords: (business: Business, action: boolean) => void;
  businessListData: BusinessList | undefined;
  isEdit: boolean;
}

const validationSchema = Yup.object().shape({
  list_name: Yup.string().required("List name is required"),
  list_headline: Yup.string().required("List headline is required"),
});

const CreateListForm = ({
  userId,
  setShowBusinesses,
  selectedBusinesses,
  handleBusinessRecords,
  businessListData,
  isEdit,
}: CreateListFormProps) => {
  const navigate = useNavigate();
  const [createList, { isLoading: createListLoading }] =
    useCreateListMutation();
  const [updateList, { isLoading: updateListLoading }] =
    useUpdateListMutation();

  const [formValues, setFormValues] = useState<BusinessListDTO>({
    list_name: businessListData?.list_name ? businessListData?.list_name : "",
    list_headline: businessListData?.list_headline
      ? businessListData?.list_headline
      : "",
    business_ids: [],
    user_id: userId,
  });

  const onSubmit = async (values: BusinessListDTO) => {
    const businessesIds = selectedBusinesses.map((business) => business.id);
    const newList: BusinessListDTO = {
      ...values,
      business_ids: businessesIds,
    };

    if (isEdit) {
      await updateList({
        id: Number(businessListData?.id),
        body: newList,
      }).unwrap();
    } else {
      await createList(newList).unwrap();
    }
    console.log("completed");
    navigate(`/businesslist/${userId}`);
  };

  return (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ errors, touched, setFieldValue }) => {
        const handleChange = (e: React.ChangeEvent<any>) => {
          const { name, value } = e.target;
          setFieldValue(name, value);
          setFormValues((prevValues: any) => ({
            ...prevValues,
            [name]: value,
          }));
        };

        return (
          <Form>
            <Box>
              {/* Item Name */}
              <Box>
                <Label label="Item Name" required={true} />
                <Field
                  as={TextField}
                  name="list_name"
                  fullWidth
                  onChange={handleChange}
                  error={touched.list_name && Boolean(errors.list_name)}
                />
                {errors.list_name && touched.list_name && (
                  <Typography color="red" fontSize="12px">
                    {errors.list_name}
                  </Typography>
                )}
                <PostScript text="List name will not be visible to your customers." />
              </Box>

              {/* Headline */}
              <Box>
                <Label label="Headline" required={true} />
                <Field
                  as={TextField}
                  name="list_headline"
                  fullWidth
                  multiline
                  rows={3}
                  onChange={handleChange}
                  error={touched.list_headline && Boolean(errors.list_headline)}
                />
                {errors.list_headline && touched.list_headline && (
                  <Typography color="red" fontSize="12px">
                    {errors.list_headline}
                  </Typography>
                )}
                <PostScript text="Your description will be visible." />
              </Box>

              {selectedBusinesses.length === 0 && (
                <Button
                  // type="submit"
                  variant="contained"
                  sx={{ width: "100%", marginTop: "20px", paddingY: "10px" }}
                  onClick={() => setShowBusinesses(true)}
                >
                  Add Businesses
                </Button>
              )}

              {/* Selected Businesses */}
              {selectedBusinesses?.length > 0 && (
                <Box sx={createBusinessListStyles.selectedBusinessContainer}>
                  <Typography sx={createBusinessListStyles.inputLabel}>
                    Businesses
                  </Typography>

                  {selectedBusinesses?.map((business: Business) => (
                    <BusinessActionCard
                      business={business}
                      key={business.id}
                      handleBusinessRecords={handleBusinessRecords}
                    />
                  ))}
                  <Box sx={createBusinessListStyles.btnContainer}>
                    <Button
                      variant="outlined"
                      sx={createBusinessListStyles.actionBtn}
                      onClick={() => setShowBusinesses(true)}
                    >
                      Add more Businesses
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      sx={createBusinessListStyles.actionBtn}
                      disabled={createListLoading || updateListLoading}
                    >
                      Save List
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateListForm;
